/**
 * Membership 5787 — household intake form validation.
 *
 * This form fills the SAME Firestore `users` document the rest of the
 * member portal reads/writes (see kbt-backend/me/me.controller.js) — it
 * does not create a parallel database. Fields per the client's build notes: household name, the
 * primary applicant's own English birthday (required) and Hebrew
 * birthday (optional), spouse (English birthday required only if a
 * spouse is named, Hebrew always optional), children (name and English
 * birthday required, Hebrew birthday optional), anniversary, yahrzeits
 * (name/date/relation), phone, email, address (as separate
 * street/city/state/zip fields).
 *
 * Design decision: Hebrew birthdays and yahrzeit dates are free-text
 * fields, not <input type="date">. The client was explicit elsewhere in
 * these notes that Hebrew-calendar dates must never be computed on the
 * frontend — the same caution applies to *storing* a Hebrew date the
 * office enters by hand (e.g. "13 Nissan" or "10 Tevet"),
 * so these are validated only as required non-blank text, not as ISO
 * dates. Anniversary is optional (not every existing household has one on
 * file) but must be a real date if provided.
 */
import * as Yup from 'yup';

// Rejects calendar-impossible strings like "2024-02-30" that the native
// Date constructor would otherwise silently roll forward into March.
// Mirrors kbt-backend/me/profileValidation.js (KBT-177) so the same rule
// is enforced on both sides.
export const isValidDateString = (value) => {
  if (!value || typeof value !== 'string') return false;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return false;

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const requiredDate = (label) =>
  Yup.string()
    .required(`${label} is required`)
    .test('valid-date', 'Enter a valid date', (v) => !v || isValidDateString(v));

const optionalDate = () =>
  Yup.string().test('valid-date-or-empty', 'Enter a valid date', (v) => !v || isValidDateString(v));

const childSchema = Yup.object({
  name: Yup.string().trim().required('Child name is required'),
  englishBirthday: requiredDate('English birthday'),
  // Optional — not every family has the Hebrew birthday on hand.
  hebrewBirthday: Yup.string().trim().nullable(),
});

const yahrzeitSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  date: Yup.string().trim().required('Date is required'),
  relation: Yup.string().trim().required('Relation is required'),
});

export const householdSchema = Yup.object({
  householdName: Yup.string().trim().required('Household name is required'),
  // The primary applicant's own birthday — required, same convention as
  // children below (English as an ISO date, Hebrew as free text since we
  // never compute Hebrew-calendar dates client-side).
  primaryEnglishBirthday: requiredDate('Your birthday'),
  // Optional — not everyone has their Hebrew birthday on hand.
  primaryHebrewBirthday: Yup.string().trim().nullable(),
  spouseName: Yup.string().nullable(),
  // Spouse birthday is only required once a spouse name is entered — a
  // single applicant with no spouse shouldn't be blocked on it.
  spouseEnglishBirthday: Yup.string().when('spouseName', {
    is: (value) => Boolean(value && value.trim()),
    then: (schema) => requiredDate('Spouse birthday'),
    otherwise: (schema) => optionalDate(),
  }),
  // Optional even once a spouse is named — only the English birthday is
  // required there; the Hebrew one is a nice-to-have, not everyone has it.
  spouseHebrewBirthday: Yup.string().trim().nullable(),
  anniversary: optionalDate(),
  phone: Yup.string().trim().required('Phone number is required'),
  email: Yup.string().trim().email('Enter a valid email').required('Email is required'),
  address: Yup.string().trim().required('Address is required'),
  city: Yup.string().trim().required('City is required'),
  state: Yup.string().trim().required('State is required'),
  zip: Yup.string()
    .trim()
    .required('ZIP code is required')
    .matches(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP code'),
  children: Yup.array().of(childSchema),
  yahrzeits: Yup.array().of(yahrzeitSchema),
});

// Turns Yup's flat `inner` error list (paths like "children[0].name") into
// a nested error shape the form can index directly:
// { householdName: '...', children: [{ name: '...' }] }.
const buildErrorTree = (innerErrors) => {
  const errors = {};
  innerErrors.forEach((e) => {
    if (!e.path) return;
    const match = /^(\w+)\[(\d+)\]\.(\w+)$/.exec(e.path);
    if (match) {
      const [, arrayField, indexStr, subField] = match;
      const index = Number(indexStr);
      if (!errors[arrayField]) errors[arrayField] = [];
      if (!errors[arrayField][index]) errors[arrayField][index] = {};
      errors[arrayField][index][subField] = e.message;
    } else if (!errors[e.path]) {
      errors[e.path] = e.message;
    }
  });
  return errors;
};

// Shared by Membership2026.js (fresh enrollment) and MyAccount.js (editing
// afterward, or filling this in for the first time from the account page)
// so both start from the exact same field shape.
export const emptyHousehold = () => ({
  householdName: '',
  primaryEnglishBirthday: '',
  primaryHebrewBirthday: '',
  spouseName: '',
  spouseEnglishBirthday: '',
  spouseHebrewBirthday: '',
  anniversary: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  children: [],
  yahrzeits: [],
});

const isFilled = (value) => typeof value === 'string' && value.trim().length > 0;

const SCALAR_HOUSEHOLD_FIELDS = [
  'householdName',
  'primaryEnglishBirthday',
  'primaryHebrewBirthday',
  'spouseName',
  'spouseEnglishBirthday',
  'spouseHebrewBirthday',
  'anniversary',
  'phone',
  'email',
  'address',
  'city',
  'state',
  'zip',
];

// Fields already on the signed-in profile, so Membership 5787 can hide
// them and only ask for what's missing.
export const knownFilledHouseholdFields = (household = {}) => {
  const filled = SCALAR_HOUSEHOLD_FIELDS.filter((field) => isFilled(household[field]));
  if (Array.isArray(household.children) && household.children.some((c) => isFilled(c?.name))) {
    filled.push('children');
  }
  if (Array.isArray(household.yahrzeits) && household.yahrzeits.some((y) => isFilled(y?.name))) {
    filled.push('yahrzeits');
  }
  return filled;
};

// Login identity fills gaps; a stored household from GET /me/household
// always wins when it has a value.
export const mergeSignedInHousehold = ({ profile, email, displayName } = {}) => {
  const stored = profile && typeof profile === 'object' ? profile : {};
  const merged = {
    ...emptyHousehold(),
    ...stored,
    children: Array.isArray(stored.children) ? stored.children : [],
    yahrzeits: Array.isArray(stored.yahrzeits) ? stored.yahrzeits : [],
  };
  if (!isFilled(merged.email) && isFilled(email)) merged.email = email;
  if (!isFilled(merged.householdName) && isFilled(displayName)) merged.householdName = displayName;
  return merged;
};

export const validateHousehold = (household) => {
  try {
    householdSchema.validateSync(household, { abortEarly: false });
    return { valid: true, errors: {} };
  } catch (err) {
    const inner = err.inner && err.inner.length ? err.inner : [err];
    return { valid: false, errors: buildErrorTree(inner) };
  }
};
